import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { useAppSystem } from '../../hooks/useAppSystem';
import { useCrmRequiredActions } from '../../hooks/useCrmRequiredActions';
import { useProposalRequiredActions } from '../../hooks/useProposalRequiredActions';
import { usePayrollRequiredActions } from '../../hooks/usePayrollRequiredActions';
import { usePayrollPeriodRequiredActions } from '../../hooks/usePayrollPeriodRequiredActions';
import { useDocumentVerificationRequiredActions } from '../../hooks/useDocumentVerificationRequiredActions';
import { useCampaignBillingRequiredActions } from '../../hooks/useCampaignBillingRequiredActions';
import { useMarketingAiTokensRequiredActions } from '../../hooks/useMarketingAiTokensRequiredActions';
import { useAgentCallbackRequiredActions } from '../../hooks/useAgentCallbackRequiredActions';
import { useDueTasksRequiredActions } from '../../hooks/useDueTasksRequiredActions';
import { useOrderRequiredActions } from '../../hooks/useOrderRequiredActions';
import { buildSalesPlayRequiredActions, useSalesPlays } from '../../hooks/useSalesPlays';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { openTodosUi } from '../../lib/assistant/todoUi';
import { useBusiness } from '../../contexts/BusinessContext';
import { isAppInstalled } from '../../lib/controlCentreApps';
import { mergeRequiredActions } from '../../lib/crm/requiredActions';
import { getProposal, type Proposal } from '../../lib/proposals';
import type { CustomerOrder } from '../../lib/customerOrders';
import { OrderDetailModal } from '../orders/CustomerOrdersTab';
import CrmActionsRequiredPanel from './CrmActionsRequiredPanel';
import ActionsSummaryModal from './ActionsSummaryModal';
import CrmEmbeddedOpportunityModal from './CrmEmbeddedOpportunityModal';
import CrmLeadQuickActionsMenu, { type QuickActionsAnchor } from './CrmLeadQuickActionsMenu';
import CallbackDetailsModal from '../CallbackDetailsModal';
import PayrollTimeRequestsModal from './PayrollTimeRequestsModal';
import RunPayrollModal from '../payroll/RunPayrollModal';
import ProposalDetailsModal from '../ProposalDetailsModal';
import DocumentSignOffModal from '../documents/DocumentSignOffModal';
import CampaignBillingRetryModal, {
  type CampaignBillingRetryTarget,
} from '../marketing/CampaignBillingRetryModal';
import { getPayrollPeriodSettings, parsePayrollPeriodRange, type PayrollPeriodSettings } from '../../lib/payrollPeriodSettings';
import { supabase } from '../../lib/supabaseClient';

const CRM_APP_SLUG = 'crm';
const PROPOSALS_APP_SLUG = 'proposals';
const PAYROLL_APP_SLUG = 'payroll';
const DOCS_APP_SLUG = 'tasks';
const MARKETING_APP_SLUG = 'marketing';

interface ActionsRequiredPanelProps {
  className?: string;
  /** 'hero' = control-centre hero styling ("Needs your attention" inbox). */
  variant?: 'light' | 'hero';
}

/** Control-centre inbox — CRM leads, approved proposals, and payroll time changes. */
export default function ActionsRequiredPanel({ className, variant = 'light' }: ActionsRequiredPanelProps) {
  const hero = variant === 'hero';
  const navigate = useNavigate();
  const { coreApps, installedAddons, loading: appsLoading } = useAppSystem();
  const { canAccess, canViewAdminDashboard } = usePermissionCheck();
  const { selectedBusiness } = useBusiness();

  const isCrmInstalled =
    !appsLoading && isAppInstalled(CRM_APP_SLUG, coreApps, installedAddons);
  const isProposalsInstalled =
    !appsLoading && isAppInstalled(PROPOSALS_APP_SLUG, coreApps, installedAddons);
  const isPayrollInstalled =
    !appsLoading && isAppInstalled(PAYROLL_APP_SLUG, coreApps, installedAddons);
  const isDocsInstalled =
    !appsLoading && isAppInstalled(DOCS_APP_SLUG, coreApps, installedAddons);
  const isMarketingInstalled =
    !appsLoading && isAppInstalled(MARKETING_APP_SLUG, coreApps, installedAddons);

  const crmLeadsEnabled = isCrmInstalled && canAccess.crm;
  // Experimental AI summary — dev builds only until it's further along.
  const summaryEnabled = crmLeadsEnabled && import.meta.env.DEV;
  const proposalsEnabled = isProposalsInstalled && canAccess.proposals;
  const payrollEnabled = isPayrollInstalled && canAccess.payroll;
  // The RPC self-gates to assigned verifiers; enable whenever Docs is installed and
  // the viewer can touch docs at all (page access or complete permission).
  const docsVerificationEnabled = isDocsInstalled && (canAccess.tasks || canAccess.docsCanComplete);
  const marketingBillingEnabled =
    isMarketingInstalled && (canAccess.marketing || canAccess.campaigns);
  const marketingAiTokensEnabled = marketingBillingEnabled;
  // Due Tasks are personal (viewer-scoped) — every signed-in user has a task world.
  const dueTasksEnabled = !!selectedBusiness?.id;
  const showActionsPanel =
    crmLeadsEnabled ||
    proposalsEnabled ||
    payrollEnabled ||
    docsVerificationEnabled ||
    marketingBillingEnabled ||
    dueTasksEnabled;

  const {
    opportunities: crmOpportunities,
    requiredActions: crmActions,
    isLoading: crmLoading,
    error: crmError,
    refresh: refreshCrm,
  } = useCrmRequiredActions(crmLeadsEnabled);

  const {
    proposalActions,
    isLoading: proposalsLoading,
    error: proposalsError,
    refresh: refreshProposals,
  } = useProposalRequiredActions(proposalsEnabled);

  const {
    payrollActions,
    isLoading: payrollLoading,
    error: payrollError,
    refresh: refreshPayroll,
  } = usePayrollRequiredActions(payrollEnabled);

  const {
    payrollPeriodActions,
    isLoading: payrollPeriodsLoading,
    error: payrollPeriodsError,
    refresh: refreshPayrollPeriods,
  } = usePayrollPeriodRequiredActions(payrollEnabled);

  const {
    documentVerificationActions,
    isLoading: docsLoading,
    error: docsError,
    refresh: refreshDocs,
  } = useDocumentVerificationRequiredActions(docsVerificationEnabled);

  const {
    campaignBillingActions,
    isLoading: campaignBillingLoading,
    error: campaignBillingError,
    refresh: refreshCampaignBilling,
  } = useCampaignBillingRequiredActions(marketingBillingEnabled);

  const {
    marketingAiTokensActions,
    isLoading: marketingAiTokensLoading,
    error: marketingAiTokensError,
    refresh: refreshMarketingAiTokens,
  } = useMarketingAiTokensRequiredActions(marketingAiTokensEnabled);

  // AI receptionist callbacks aren't tied to an installed app — a pending
  // callback means a caller is waiting, so it always belongs in the inbox.
  // Admin-only: staff without admin access don't handle receptionist callbacks.
  const callbacksEnabled = !appsLoading && !!selectedBusiness?.id && canViewAdminDashboard;
  const {
    callbackActions,
    isLoading: callbacksLoading,
    error: callbacksError,
    refresh: refreshCallbacks,
  } = useAgentCallbackRequiredActions(callbacksEnabled);

  const {
    dueTaskActions,
    isLoading: dueTasksLoading,
    error: dueTasksError,
    refresh: refreshDueTasks,
  } = useDueTasksRequiredActions(dueTasksEnabled);

  // Online orders sitting in "new" wait for approval — always inbox-worthy.
  const orderApprovalsEnabled = !appsLoading && !!selectedBusiness?.id && canViewAdminDashboard;
  const {
    pendingOrders,
    orderActions,
    isLoading: orderApprovalsLoading,
    error: orderApprovalsError,
    refresh: refreshOrderApprovals,
  } = useOrderRequiredActions(orderApprovalsEnabled);

  // Moves the Sales manager has staged on leads. They ride under the lead's own
  // row when it has one; a lead with nothing else due gets a row of its own.
  const { playsByOpportunity, holdPlay, settlePlay, failPlay, pendingOpportunityIds, refresh: refreshPlays } = useSalesPlays(crmLeadsEnabled);

  // Realtime is the fast path. This pull covers a missed event so a play Sales
  // just staged shows up without a page refresh. Hidden tabs do not poll.
  const refreshInboxRef = useRef<() => void>(() => {});
  refreshInboxRef.current = () => {
    const jobs = [
      crmLeadsEnabled ? refreshCrm() : null,
      crmLeadsEnabled ? refreshPlays() : null,
      proposalsEnabled ? refreshProposals() : null,
      payrollEnabled ? refreshPayroll() : null,
      payrollEnabled ? refreshPayrollPeriods() : null,
      docsVerificationEnabled ? refreshDocs() : null,
      marketingBillingEnabled ? refreshCampaignBilling() : null,
      marketingAiTokensEnabled ? refreshMarketingAiTokens() : null,
      callbacksEnabled ? refreshCallbacks() : null,
      dueTasksEnabled ? refreshDueTasks() : null,
      orderApprovalsEnabled ? refreshOrderApprovals() : null,
    ];
    for (const job of jobs) void Promise.resolve(job).catch(() => {});
  };
  useEffect(() => {
    if (!showActionsPanel) return;
    const pull = () => {
      if (document.visibilityState !== 'visible') return;
      refreshInboxRef.current();
    };
    const timer = window.setInterval(pull, 8_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') pull();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [showActionsPanel]);

  const salesPlayActions = useMemo(() => {
    if (!playsByOpportunity.size) return [];
    const covered = new Set<number>();
    for (const item of [...crmActions.followUpDue, ...crmActions.newLeads, ...crmActions.noActivity]) {
      if (item.opportunityId != null) covered.add(item.opportunityId);
    }
    const byId = new Map(crmOpportunities.map((opp) => [opp.id, opp]));
    return buildSalesPlayRequiredActions([...playsByOpportunity.values()], covered).map((item) => {
      const opp = item.opportunityId != null ? byId.get(item.opportunityId) : undefined;
      if (!opp || typeof opp.lead_score !== 'number') return item;
      return { ...item, leadScore: opp.lead_score, leadScoreReason: opp.lead_score_reason ?? null };
    });
  }, [playsByOpportunity, crmActions, crmOpportunities]);

  // A staged play is the one card for that lead. The separate "convert to booking"
  // row stays when Sales has not lined up a move, or the quote was never tied to a lead.
  const proposalConvert = useMemo(() => {
    if (!playsByOpportunity.size) return proposalActions;
    const bookedQuoteIds = new Set(
      [...playsByOpportunity.values()]
        .map((play) => play.booking_proposal_id)
        .filter((id): id is string => Boolean(id)),
    );
    return proposalActions.filter((item) => {
      if (item.opportunityId != null && playsByOpportunity.has(item.opportunityId)) return false;
      if (item.proposalId && bookedQuoteIds.has(item.proposalId)) return false;
      return true;
    });
  }, [proposalActions, playsByOpportunity]);

  const [payrollSettings, setPayrollSettings] = useState<PayrollPeriodSettings | null>(null);
  const [signOffSubmissionId, setSignOffSubmissionId] = useState<string | null>(null);
  const [retryCampaign, setRetryCampaign] = useState<CampaignBillingRetryTarget | null>(null);
  const [retryCampaignLoading, setRetryCampaignLoading] = useState(false);

  useEffect(() => {
    if (!payrollEnabled || !selectedBusiness?.id) {
      setPayrollSettings(null);
      return;
    }

    let cancelled = false;
    getPayrollPeriodSettings(selectedBusiness.id)
      .then((settings) => {
        if (!cancelled) setPayrollSettings(settings);
      })
      .catch((err) => {
        console.error('Error loading payroll settings for actions required:', err);
        if (!cancelled) setPayrollSettings(null);
      });

    return () => {
      cancelled = true;
    };
  }, [payrollEnabled, selectedBusiness?.id]);

  const requiredActions = useMemo(
    () =>
      mergeRequiredActions(crmActions, {
        proposalConvert,
        payrollRequests: payrollActions,
        payrollPeriods: payrollPeriodActions,
        documentVerifications: documentVerificationActions,
        campaignBillingFailed: campaignBillingActions,
        marketingAiTokensBlocked: marketingAiTokensActions,
        agentCallbacks: callbackActions,
        dueTasks: dueTaskActions,
        orderApprovals: orderActions,
        salesPlays: salesPlayActions,
      }),
    [
      crmActions,
      proposalConvert,
      payrollActions,
      payrollPeriodActions,
      documentVerificationActions,
      campaignBillingActions,
      marketingAiTokensActions,
      callbackActions,
      dueTaskActions,
      orderActions,
      salesPlayActions,
    ],
  );

  const visibleActions = useMemo(() => {
    if (!pendingOpportunityIds.size) return requiredActions;
    const keep = <T extends { opportunityId?: number | null }>(items: T[]) =>
      items.filter((item) => item.opportunityId == null || !pendingOpportunityIds.has(item.opportunityId));
    const followUpDue = keep(requiredActions.followUpDue);
    const newLeads = keep(requiredActions.newLeads);
    const noActivity = keep(requiredActions.noActivity);
    const proposalConvert = keep(requiredActions.proposalConvert);
    const salesPlays = keep(requiredActions.salesPlays);
    const removed =
      requiredActions.followUpDue.length - followUpDue.length +
      requiredActions.newLeads.length - newLeads.length +
      requiredActions.noActivity.length - noActivity.length +
      requiredActions.proposalConvert.length - proposalConvert.length +
      requiredActions.salesPlays.length - salesPlays.length;
    return { ...requiredActions, followUpDue, newLeads, noActivity, proposalConvert, salesPlays, total: requiredActions.total - removed };
  }, [requiredActions, pendingOpportunityIds]);

  const [detailOpportunityId, setDetailOpportunityId] = useState<number | null>(null);
  /** Lead row long-pressed for quick actions, with the row's on-screen box. */
  const [quickActions, setQuickActions] = useState<{
    opportunityId: number;
    title: string;
    anchor: QuickActionsAnchor;
  } | null>(null);
  const [selectedApprovalOrder, setSelectedApprovalOrder] = useState<CustomerOrder | null>(null);
  const [selectedCallbackId, setSelectedCallbackId] = useState<string | null>(null);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [payrollRequestsModalOpen, setPayrollRequestsModalOpen] = useState(false);
  const [runPayrollModalOpen, setRunPayrollModalOpen] = useState(false);
  const [runPayrollInitialPeriod, setRunPayrollInitialPeriod] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [runPayrollModalKey, setRunPayrollModalKey] = useState(0);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    await Promise.all([
      refreshCrm(),
      refreshProposals(),
      refreshPayroll(),
      refreshPayrollPeriods(),
      refreshDocs(),
      refreshCampaignBilling(),
      refreshMarketingAiTokens(),
      refreshCallbacks(),
      refreshDueTasks(),
      refreshOrderApprovals(),
    ]);
  }, [
    refreshCrm,
    refreshProposals,
    refreshPayroll,
    refreshPayrollPeriods,
    refreshDocs,
    refreshCampaignBilling,
    refreshMarketingAiTokens,
    refreshCallbacks,
    refreshDueTasks,
    refreshOrderApprovals,
  ]);

  const handleSelectOpportunity = (opportunityId: number) => {
    setDetailOpportunityId(opportunityId);
  };

  const handleSelectOrder = (orderId: string) => {
    const order = pendingOrders.find((o) => o.id === orderId);
    if (order) setSelectedApprovalOrder(order);
  };

  const handleSelectProposal = async (proposalId: string) => {
    setProposalLoading(true);
    try {
      const proposal = await getProposal(proposalId);
      setSelectedProposal(proposal);
      setProposalModalOpen(true);
    } catch (err) {
      console.error('Error loading proposal for actions required:', err);
    } finally {
      setProposalLoading(false);
    }
  };

  const handleSelectCampaignBilling = async (campaignId: string) => {
    setRetryCampaignLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_campaigns')
        .select(
          'id, name, recipient_count, billing_status, billing_payment_intent_id, scheduled_at, channel_type',
        )
        .eq('id', campaignId)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Campaign not found');
      setRetryCampaign(data as CampaignBillingRetryTarget);
    } catch (err) {
      console.error('Error loading campaign for billing retry:', err);
    } finally {
      setRetryCampaignLoading(false);
    }
  };

  const handleSelectPayrollRequest = () => {
    setPayrollRequestsModalOpen(true);
  };

  const handleSelectPayrollPeriod = (periodStart: string, periodEnd: string) => {
    const period = parsePayrollPeriodRange(periodStart, periodEnd);
    setRunPayrollInitialPeriod(period);
    setRunPayrollModalKey((key) => key + 1);
    setRunPayrollModalOpen(true);
  };

  const handleCloseRunPayrollModal = () => {
    setRunPayrollModalOpen(false);
    setRunPayrollInitialPeriod(null);
  };

  const combinedError =
    crmError ||
    proposalsError ||
    payrollError ||
    payrollPeriodsError ||
    docsError ||
    campaignBillingError ||
    marketingAiTokensError ||
    callbacksError ||
    dueTasksError ||
    orderApprovalsError;
  const combinedLoading =
    appsLoading ||
    crmLoading ||
    proposalsLoading ||
    payrollLoading ||
    payrollPeriodsLoading ||
    docsLoading ||
    campaignBillingLoading ||
    marketingAiTokensLoading ||
    callbacksLoading ||
    dueTasksLoading ||
    orderApprovalsLoading ||
    proposalLoading ||
    retryCampaignLoading;

  const hasAnyAppInstalled =
    isCrmInstalled ||
    isProposalsInstalled ||
    isPayrollInstalled ||
    isDocsInstalled ||
    isMarketingInstalled;

  // Only show the blocking spinner on a true cold start (nothing to show yet). Once any
  // source resolves (or cached data is present), render what we have and let the rest
  // fill in — avoids waiting on the slowest source (payroll periods) before showing anything.
  const showLoadingSpinner = combinedLoading && requiredActions.total === 0;

  return (
    <section
      className={`relative z-0 overflow-hidden flex flex-col h-full min-h-0 max-h-full ${
        hero ? '' : 'max-lg:!rounded-t-none max-lg:!border-t-0'
      } ${className ?? ''}`}
      data-control-centre-panel={hero ? undefined : true}
      aria-label="Actions required"
    >
      {!showActionsPanel ? (
        <>
          <header className="flex items-center gap-3 px-3 py-2.5 min-w-0 shrink-0 sm:p-4">
            <div
              className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg sm:rounded-xl bg-gray-50 border border-gray-100 text-gray-800"
              aria-hidden
            >
              <ClipboardList className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-[15px] sm:text-sm font-semibold text-gray-900 truncate leading-tight">
                Actions Required
              </h2>
              <p className="hidden sm:block text-xs text-gray-500 mt-0.5 truncate">
                Items that need your attention
              </p>
            </div>
          </header>
          <div className="control-centre-panel__body max-lg:hidden flex-1 flex flex-col items-center justify-center text-center min-h-0 px-4">
            {appsLoading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : !hasAnyAppInstalled ? (
              <>
                <p className="text-sm font-medium text-gray-800">No apps installed</p>
                <p className="text-xs text-gray-500 mt-1 max-w-[220px]">
                  Install CRM, Proposals, Payroll, or Marketing from Nexus Connect to see actions here.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-800">Access required</p>
                <p className="text-xs text-gray-500 mt-1 max-w-[220px]">
                  You do not have permission to view actions for the installed apps.
                </p>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden max-lg:flex-none">
          {combinedError ? (
            <p className="px-4 py-3 text-xs text-red-600 shrink-0">{combinedError}</p>
          ) : null}
          <CrmActionsRequiredPanel
            actions={visibleActions}
            variant={variant}
            loading={showLoadingSpinner}
            onSelectOpportunity={crmLeadsEnabled ? handleSelectOpportunity : undefined}
            onSelectProposal={proposalsEnabled ? handleSelectProposal : undefined}
            onSelectPayrollRequest={payrollEnabled ? handleSelectPayrollRequest : undefined}
            onSelectPayrollPeriod={payrollEnabled ? handleSelectPayrollPeriod : undefined}
            onSelectDocumentVerification={docsVerificationEnabled ? (id) => setSignOffSubmissionId(id) : undefined}
            onSelectCampaignBilling={
              marketingBillingEnabled ? handleSelectCampaignBilling : undefined
            }
            onSelectMarketingAiTokens={
              marketingAiTokensEnabled ? () => navigate('/ai-settings') : undefined
            }
            onSelectCallback={(item) => {
              // Callbacks always open the callback modal first — details plus
              // the call button. The lead (if any) is reachable from there.
              if (item.callbackId) setSelectedCallbackId(item.callbackId);
            }}
            onSelectDueTask={(item) => openTodosUi(item.todoId ? { editTodoId: item.todoId } : undefined)}
            onSelectOrder={(item) => {
              if (item.orderId) handleSelectOrder(item.orderId);
            }}
            salesPlays={crmLeadsEnabled ? playsByOpportunity : undefined}
            onSalesPlayActed={(playId) => {
              holdPlay(playId);
            }}
            onSalesPlaySettled={(playId) => {
              void Promise.resolve(refreshCrm())
                .catch(() => {})
                .finally(() => settlePlay(playId));
            }}
            onSalesPlayFailed={(playId, message) => {
              failPlay(playId, message);
            }}
            onLeadQuickActions={
              crmLeadsEnabled
                ? (item, anchor) => {
                    if (item.opportunityId == null) return;
                    setQuickActions({
                      opportunityId: item.opportunityId,
                      title: item.title,
                      anchor,
                    });
                  }
                : undefined
            }
            onRequestSummary={summaryEnabled ? () => setSummaryModalOpen(true) : undefined}
            fillHeight
            collapsible
            className="border-0 shadow-none rounded-none"
          />
        </div>
      )}

      {summaryEnabled ? (
        <ActionsSummaryModal
          isOpen={summaryModalOpen}
          onClose={() => setSummaryModalOpen(false)}
          businessId={selectedBusiness?.id}
          businessName={selectedBusiness?.name}
        />
      ) : null}

      {detailOpportunityId !== null ? (
        <CrmEmbeddedOpportunityModal
          opportunityId={detailOpportunityId}
          onClose={() => setDetailOpportunityId(null)}
          onUpdated={() => void refresh()}
        />
      ) : null}

      <CallbackDetailsModal
        callbackId={selectedCallbackId}
        onClose={() => setSelectedCallbackId(null)}
        onViewLead={(opportunityId) => handleSelectOpportunity(opportunityId)}
      />

      {signOffSubmissionId ? (
        <DocumentSignOffModal
          submissionId={signOffSubmissionId}
          onClose={() => setSignOffSubmissionId(null)}
          onSigned={() => void refreshDocs()}
        />
      ) : null}

      {selectedProposal && proposalModalOpen ? (
        <ProposalDetailsModal
          isOpen={proposalModalOpen}
          onClose={() => {
            setProposalModalOpen(false);
            setSelectedProposal(null);
          }}
          proposal={selectedProposal}
          onUpdate={(updatedProposal) => {
            setSelectedProposal(updatedProposal);
            void refresh();
          }}
          onDelete={() => {
            setProposalModalOpen(false);
            setSelectedProposal(null);
            void refresh();
          }}
        />
      ) : null}

      {selectedBusiness?.id && retryCampaign ? (
        <CampaignBillingRetryModal
          isOpen
          businessId={selectedBusiness.id}
          campaign={retryCampaign}
          onClose={() => setRetryCampaign(null)}
          onSuccess={() => void refreshCampaignBilling()}
        />
      ) : null}

      {selectedBusiness?.id && quickActions ? (
        <CrmLeadQuickActionsMenu
          businessId={selectedBusiness.id}
          opportunityId={quickActions.opportunityId}
          leadTitle={quickActions.title}
          anchor={quickActions.anchor}
          onClose={() => setQuickActions(null)}
          onChanged={() => void refresh()}
        />
      ) : null}

      {selectedBusiness?.id && selectedApprovalOrder ? (
        <OrderDetailModal
          order={selectedApprovalOrder}
          businessId={selectedBusiness.id}
          onClose={() => setSelectedApprovalOrder(null)}
          onChanged={() => void refreshOrderApprovals()}
          onInvoiced={() => void refreshOrderApprovals()}
        />
      ) : null}

      {selectedBusiness?.id && payrollEnabled ? (
        <PayrollTimeRequestsModal
          isOpen={payrollRequestsModalOpen}
          onClose={() => setPayrollRequestsModalOpen(false)}
          businessId={selectedBusiness.id}
          onRequestProcessed={() => void refreshPayroll()}
        />
      ) : null}

      {selectedBusiness?.id && payrollEnabled ? (
        <RunPayrollModal
          key={`run-payroll-${runPayrollModalKey}`}
          isOpen={runPayrollModalOpen}
          onClose={handleCloseRunPayrollModal}
          businessId={selectedBusiness.id}
          payrollSettings={payrollSettings}
          initialPeriod={runPayrollInitialPeriod}
          onPayrollComplete={() => {
            void refreshPayrollPeriods();
            void refreshPayroll();
          }}
        />
      ) : null}
    </section>
  );
}
